import { useRef, useState } from 'react';
import { useFinancieelStore } from '../../stores';
import { KasBron } from './Kas';
import { useKasSaldoStore } from '../../stores/kas-saldo-store';
import type { BankConnection } from '../../stores/financieel-store';

/**
 * Alleen een bruikbaar kassaldo telt mee in het totaal. Twee redenen om het
 * eruit te laten: zonder anker is het getal een mutatie en geen stand, en een
 * anker dat de kas ergens negatief maakt is aantoonbaar fout. Een aanname mag
 * wél meetellen — die is onzeker, niet onmogelijk — maar wordt gelabeld.
 * Primitief (of null) zodat deze component niet meerendert op de kas-store.
 */
const selectGeankerdKassaldo = (s: {
  saldo: { heeftAnker: boolean; saldo: number; controle: { houdbaar: boolean } } | null;
}) => (s.saldo?.heeftAnker && s.saldo.controle.houdbaar ? s.saldo.saldo : null);

const selectKasIsSchatting = (s: { saldo: { ankerSoort: string | null } | null }) =>
  s.saldo?.ankerSoort === 'aanname';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
}

function daysSince(dateStr: string): number {
  if (!dateStr) return 999;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function SourceIndicator({ bron, dateStr }: { bron?: 'live' | 'upload'; dateStr: string }) {
  if (bron === 'live') {
    const days = daysSince(dateStr);
    const fresh = days <= 1;
    return (
      <span className={`text-xs flex items-center gap-1 ${fresh ? 'text-emerald-600' : 'text-emerald-500'}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
        Live{!fresh && ` (${days}d)`}
      </span>
    );
  }
  const days = daysSince(dateStr);
  if (days <= 3) return <span className="text-emerald-600 text-xs">actueel</span>;
  if (days <= 7) return <span className="text-amber-600 text-xs">{days}d oud</span>;
  return <span className="text-red-600 text-xs">{days}d oud</span>;
}

export function CashPositie() {
  const {
    mollieBalance, myposBalance, bankSaldi, openstaandDebiteuren,
    isLoading, loadOverview, selectedMonth, uploadBankBestand,
    bankConnections, refreshLiveBalances, isRefreshing, initMyposBalance,
    startBankAuth, isConnecting,
  } = useFinancieelStore();
  const [renewingId, setRenewingId] = useState<string | null>(null);

  // Re-auth bij Enable Banking: redirect naar ABN-login; de callback op
  // /financieel (?code=) rondt af en ruimt de oude connection-rij op.
  const handleRenewConsent = async (conn: BankConnection) => {
    setRenewingId(conn.id);
    const url = await startBankAuth(conn.institution_id || 'ABN AMRO', conn.institution_name);
    if (url) {
      window.location.href = url;
    } else {
      setRenewingId(null);
    }
  };

  const consentDaysLeft = (conn: BankConnection): number | null => {
    if (!conn.consent_expires_at) return null;
    return Math.floor((new Date(conn.consent_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  };

  // Vernieuw-blok tonen zodra er iets te doen is: verlopen, of <14 dagen resterend.
  const connectionsNeedingRenewal = bankConnections.filter((c) => {
    if (c.status === 'expired') return true;
    if (c.status !== 'linked') return false;
    const days = consentDaysLeft(c);
    return days !== null && days < 14;
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<string>('');
  const [showMyposInit, setShowMyposInit] = useState(false);
  const [myposInitValue, setMyposInitValue] = useState('');
  const [myposInitLoading, setMyposInitLoading] = useState(false);

  const kasSaldo = useKasSaldoStore(selectGeankerdKassaldo);
  const kasIsSchatting = useKasSaldoStore(selectKasIsSchatting);

  const bankTotaal = bankSaldi.reduce((sum, b) => sum + b.saldo, 0);
  const providerTotaal = (mollieBalance?.available || 0) + (mollieBalance?.pending || 0) + (myposBalance || 0);
  const totaal = bankTotaal + providerTotaal + (kasSaldo ?? 0);

  const handleUploadClick = (rekeningNaam: string) => {
    uploadTargetRef.current = rekeningNaam;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadBankBestand(file, uploadTargetRef.current);
    e.target.value = '';
  };

  return (
    <div className="bg-white rounded-card shadow-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-main">Cash Positie</h2>
        <button
          onClick={() => refreshLiveBalances()}
          disabled={isLoading || isRefreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 transition-colors"
        >
          <svg className={`w-4 h-4 ${isLoading || isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Ververs
        </button>
      </div>

      {/* Bank accounts */}
      <div className="mb-4">
        <h3 className="text-xs font-medium text-muted uppercase tracking-wider mb-2">Bankrekeningen</h3>
        <div className="space-y-2">
          {/* Consent vernieuwen — PSD2-consent verloopt elke 90 dagen, per rekening */}
          {connectionsNeedingRenewal.length > 0 && (
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-2 space-y-2">
              <p className="text-xs font-medium text-amber-800">
                Bankkoppeling vernieuwen — je wordt per rekening doorgestuurd naar de ABN-login
                (consent is daarna weer 90 dagen geldig)
              </p>
              {connectionsNeedingRenewal.map((conn) => {
                const days = consentDaysLeft(conn);
                const expired = conn.status === 'expired' || (days !== null && days < 0);
                const label = conn.accounts?.[0]?.name || conn.institution_name;
                return (
                  <div key={conn.id} className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs text-main truncate">{label}</span>
                      <span
                        className={`text-[11px] px-1.5 py-0.5 rounded shrink-0 ${
                          expired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {expired ? 'verlopen' : `nog ${days} ${days === 1 ? 'dag' : 'dagen'}`}
                      </span>
                    </div>
                    <button
                      onClick={() => handleRenewConsent(conn)}
                      disabled={isConnecting}
                      className="px-2.5 py-1 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 shrink-0"
                    >
                      {renewingId === conn.id && isConnecting ? 'Doorsturen…' : 'Vernieuw'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {bankSaldi.length === 0 ? (
            <p className="text-sm text-gray-400">Geen bankdata beschikbaar voor {selectedMonth}</p>
          ) : (
            bankSaldi.map((bank) => (
              <div key={bank.iban} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-main">{bank.naam}</span>
                  <SourceIndicator bron={(bank as any).bron} dateStr={bank.datum} />
                </div>
                <span className="text-sm font-medium tabular-nums">{formatCurrency(bank.saldo)}</span>
              </div>
            ))
          )}
          <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
            <span className="text-sm font-medium text-muted">Subtotaal bank</span>
            <span className="text-sm font-semibold tabular-nums">{formatCurrency(bankTotaal)}</span>
          </div>
        </div>
      </div>

      {/* Payment providers */}
      <div className="mb-4">
        <h3 className="text-xs font-medium text-muted uppercase tracking-wider mb-2">Payment Providers</h3>
        <div className="space-y-2">
          {mollieBalance && (
            <div className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm text-main">Mollie</span>
                {mollieBalance.error || mollieBalance.available === null ? (
                  <span className="text-xs text-amber-600" title={mollieBalance.error || 'saldo onbekend'}>geen toegang</span>
                ) : (
                  <span className="text-xs text-gray-400">beschikbaar</span>
                )}
              </div>
              <span className="text-sm font-medium tabular-nums">
                {mollieBalance.error || mollieBalance.available === null ? '—' : formatCurrency(mollieBalance.available)}
              </span>
            </div>
          )}
          {mollieBalance && mollieBalance.pending > 0 && (
            <div className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Mollie</span>
                <span className="text-xs text-gray-400">pending</span>
              </div>
              <span className="text-sm font-medium tabular-nums text-gray-500">{formatCurrency(mollieBalance.pending)}</span>
            </div>
          )}
          {myposBalance !== null ? (
            <div className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm text-main">MyPos</span>
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                  berekend
                </span>
              </div>
              <span className="text-sm font-medium tabular-nums">{formatCurrency(myposBalance)}</span>
            </div>
          ) : (
            <div className="py-1.5">
              {!showMyposInit ? (
                <button
                  onClick={() => setShowMyposInit(true)}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  MyPos saldo instellen
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-main">MyPos</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Saldo"
                    value={myposInitValue}
                    onChange={(e) => setMyposInitValue(e.target.value)}
                    className="w-28 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    autoFocus
                  />
                  <button
                    disabled={myposInitLoading || !myposInitValue}
                    onClick={async () => {
                      setMyposInitLoading(true);
                      const ok = await initMyposBalance(parseFloat(myposInitValue));
                      setMyposInitLoading(false);
                      if (ok) {
                        setShowMyposInit(false);
                        setMyposInitValue('');
                      }
                    }}
                    className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {myposInitLoading ? '...' : 'Opslaan'}
                  </button>
                  <button
                    onClick={() => { setShowMyposInit(false); setMyposInitValue(''); }}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Annuleer
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-between py-1.5 border-t border-gray-100">
            <span className="text-sm font-medium text-muted">Subtotaal providers</span>
            <span className="text-sm font-semibold tabular-nums">{formatCurrency(providerTotaal)}</span>
          </div>
        </div>
      </div>

      {/* Kas — contant geld, in dezelfde opmaak als de andere bronnen */}
      <div className="mb-4">
        <h3 className="text-xs font-medium text-muted uppercase tracking-wider mb-2">Kas (contant)</h3>
        <KasBron />
      </div>

      {/* Grand total */}
      <div className="border-t-2 border-gray-200 pt-3 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-main">Totaal cash positie</span>
          <span className="text-lg font-bold tabular-nums text-main">{formatCurrency(totaal)}</span>
        </div>
        {kasIsSchatting && kasSaldo !== null ? (
          <p className="text-xs text-muted mt-1">
            Het kasdeel berust op een aangenomen startsaldo en is dus een schatting.
          </p>
        ) : null}
      </div>

      {/* Openstaande debiteuren */}
      {openstaandDebiteuren > 0 && (
        <div className="flex items-center justify-between py-1.5 text-sm">
          <span className="text-gray-500">Openstaande debiteuren (90d)</span>
          <span className="text-amber-600 font-medium tabular-nums">{formatCurrency(openstaandDebiteuren)}</span>
        </div>
      )}

      {/* Hidden file input for bank uploads (kept as fallback) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xml"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
